import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Alert,
  alpha,
  Chip,
  Card,
  CardContent,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import { 
  Refresh, 
  Build as BuildIcon, 
  History, 
  Rocket, 
  CalendarToday, 
  Code,
  Edit,
  Save,
  Add,
  Delete,
  ExpandMore,
  ExpandLess,
  GitHub,
  Commit as CommitIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { SectionTitle, GlassContainer, fadeInUp } from '../styles/sharedStyles';
import GuiThemeSettings from './GuiThemeSettings';
import LogonPageSettings from './LogonPageSettings';
import SidebarSettings from './SidebarSettings';

const MaintenanceLogs = ({ apiBaseUrl, token }) => {
  const { t } = useTranslation();
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const scrollRef = useRef(null);

  const fetchLogs = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${apiBaseUrl}/api/logs`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      setLines(response.data.lines || []);
    } catch (err) {
      setError(err.response?.status === 401 ? t('maintenance.sessionExpired') : err.message || t('maintenance.failedToLoadLogs'));
      if (err.response?.status === 401) return;
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchLogs, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines]);

  const styleLine = (line) => {
    if (!line) return { color: 'rgba(255,255,255,0.85)' };
    if (line.includes('❌') || line.includes('ERROR') || line.toLowerCase().includes('error')) return { color: '#ff6b6b' };
    if (line.includes('✅') || line.includes('SUCCESS') || line.toLowerCase().includes('success')) return { color: '#69db7c' };
    if (line.includes('🔍') || line.includes('🔐') || line.includes('📤')) return { color: '#74c0fc' };
    if (line.includes('⏰') || line.includes('🔄')) return { color: '#ffd43b' };
    return { color: 'rgba(255,255,255,0.85)' };
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {t('maintenance.appLogs')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            {autoRefresh ? t('maintenance.autoRefresh') : t('maintenance.paused')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setAutoRefresh((a) => !a)}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
            title={autoRefresh ? t('maintenance.pauseAutoRefresh') : t('maintenance.resumeAutoRefresh')}
          >
            {autoRefresh ? '⏸' : '▶'}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => { setLoading(true); fetchLogs(); }}
            disabled={loading}
            sx={{ color: '#00d4ff' }}
            title="Refresh logs"
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        ref={scrollRef}
        sx={{
          background: 'rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: 2,
          p: 2,
          fontFamily: 'Consolas, "Courier New", monospace',
          fontSize: '0.8rem',
          lineHeight: 1.5,
          maxHeight: '70vh',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
        }}
      >
        {loading && lines.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.6)' }}>
            <CircularProgress size={18} sx={{ color: '#00d4ff' }} />
            <span>{t('maintenance.loadingLogs')}</span>
          </Box>
        ) : lines.length === 0 ? (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{t('maintenance.noLogsYet')}</Typography>
        ) : (
          lines.map((line, i) => (
            <div key={i} style={styleLine(line)}>
              {line}
            </div>
          ))
        )}
      </Box>
    </Box>
  );
};

const Roadmap = ({ apiBaseUrl, token }) => {
  const [futureChanges, setFutureChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchRoadmap = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${apiBaseUrl}/api/changelog`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data || { commits: [], future_changes: [] };
      setFutureChanges(data.future_changes || []);
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expired' : err.message || 'Failed to load roadmap');
      if (err.response?.status === 401) return;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchRoadmap();
  }, [fetchRoadmap]);

  const handleEditFuture = () => {
    const changesText = (futureChanges || []).map(item => item.message).join('\n');
    setEditValue(changesText);
    setEditDialogOpen(true);
  };

  const handleSaveFuture = async () => {
    setSaving(true);
    try {
      const changes = editValue.split('\n').filter(line => line.trim() !== '');
      await axios.post(`${apiBaseUrl}/api/changelog/future`, 
        { changes },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setEditDialogOpen(false);
      fetchRoadmap();
    } catch (err) {
      setError('Failed to save roadmap: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '16px',
              background: 'rgba(255, 212, 59, 0.1)',
              border: '1px solid rgba(255, 212, 59, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Rocket sx={{ color: '#ffd43b', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
              Roadmap
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Planned features & improvements
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => { setLoading(true); fetchRoadmap(); }}
            disabled={loading}
            sx={{ 
              color: '#ffd43b',
              background: 'rgba(255, 212, 59, 0.1)',
              border: '1px solid rgba(255, 212, 59, 0.1)',
              '&:hover': { background: 'rgba(255, 212, 59, 0.2)', borderColor: 'rgba(255, 212, 59, 0.3)' }
            }}
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
          <IconButton
            onClick={handleEditFuture}
            sx={{ 
              color: '#ffd43b',
              background: 'rgba(255, 212, 59, 0.1)',
              border: '1px solid rgba(255, 212, 59, 0.1)',
              '&:hover': { background: 'rgba(255, 212, 59, 0.2)', borderColor: 'rgba(255, 212, 59, 0.3)' }
            }}
          >
            <Edit />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px', 
            background: 'rgba(211, 47, 47, 0.1)', 
            color: '#ff8a80',
            border: '1px solid rgba(211, 47, 47, 0.2)',
            '& .MuiAlert-icon': { color: '#ff8a80' }
          }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#ffd43b' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>Loading roadmap...</Typography>
        </Box>
      ) : (
        <Box 
          sx={{ 
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: '24px',
            p: 3,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {futureChanges && futureChanges.length > 0 ? (
              futureChanges.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.02)',
                    border: '1px solid rgba(255, 255, 255, 0.04)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'rgba(255, 212, 59, 0.03)',
                      borderColor: 'rgba(255, 212, 59, 0.1)',
                    }
                  }}
                >
                  <Box
                    sx={{
                      mt: 1,
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: '#ffd43b',
                      boxShadow: '0 0 8px rgba(255, 212, 59, 0.6)',
                      flexShrink: 0
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.85)', 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      lineHeight: 1.4
                    }}
                  >
                    {item.message}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', py: 4, opacity: 0.3 }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  No items in roadmap
                </Typography>
              </Box>
            )}
          </Box>

          <Box 
            sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid rgba(255,255,255,0.06)',
              textAlign: 'center'
            }}
          >
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', display: 'block', mb: 1 }}>
              WANT TO CONTRIBUTE?
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GitHub />}
              sx={{
                borderRadius: '8px',
                borderColor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.6)',
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: '#fff',
                  color: '#fff',
                  background: 'rgba(255,255,255,0.05)'
                }
              }}
              onClick={() => window.open('https://github.com', '_blank')}
            >
              Source Code
            </Button>
          </Box>
        </Box>
      )}

      {/* Edit Roadmap Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: '#1a1a1a',
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
            color: '#fff',
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Rocket sx={{ color: '#ffd43b' }} />
            Edit Roadmap
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2, display: 'block' }}>
            Enter each planned change on a new line. These will be saved to CHANGELOG.md.
          </Typography>
          <TextField
            multiline
            rows={10}
            fullWidth
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            variant="outlined"
            placeholder="e.g.\n- Implement backup encryption\n- Add dark mode support"
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.2)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                '&.Mui-focused fieldset': { borderColor: '#ffd43b' },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <Button 
            onClick={() => setEditDialogOpen(false)} 
            disabled={saving}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveFuture} 
            disabled={saving}
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <Save />}
            sx={{ 
              background: '#ffd43b', 
              color: '#000',
              fontWeight: 600,
              '&:hover': { background: '#fcc419' },
              '&.Mui-disabled': { background: 'rgba(255, 212, 59, 0.3)' }
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Changelog = ({ apiBaseUrl, token }) => {
  const [data, setData] = useState({ commits: [], future_changes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCommits, setExpandedCommits] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchChangelog = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${apiBaseUrl}/api/changelog`, {
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      setData(response.data || { commits: [], future_changes: [] });
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expired' : err.message || 'Failed to load changelog');
      if (err.response?.status === 401) return;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchChangelog();
  }, [fetchChangelog]);

  const toggleCommitExpand = (hash) => {
    setExpandedCommits(prev => ({
      ...prev,
      [hash]: !prev[hash]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '16px',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            }}
          >
            <History sx={{ color: '#00d4ff', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.5px' }}>
              System Changelog
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <GitHub sx={{ fontSize: 14 }} /> Live Git History
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={() => { setLoading(true); fetchChangelog(); }}
            disabled={loading}
            sx={{ 
              color: '#00d4ff',
              background: 'rgba(0, 212, 255, 0.1)',
              border: '1px solid rgba(0, 212, 255, 0.1)',
              '&:hover': { background: 'rgba(0, 212, 255, 0.2)', borderColor: 'rgba(0, 212, 255, 0.3)' }
            }}
          >
            <Refresh sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: '12px', 
            background: 'rgba(211, 47, 47, 0.1)', 
            color: '#ff8a80',
            border: '1px solid rgba(211, 47, 47, 0.2)',
            '& .MuiAlert-icon': { color: '#ff8a80' }
          }}
        >
          {error}
        </Alert>
      )}

      {loading && data.commits.length === 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10, gap: 2 }}>
          <CircularProgress size={40} thickness={4} sx={{ color: '#00d4ff' }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.9rem' }}>Fetching repository data...</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Git Commits Section - Recent Updates */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
              <CommitIcon sx={{ color: '#00d4ff' }} />
              <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600, letterSpacing: '0.5px' }}>
                RECENT UPDATES
              </Typography>
              <Chip 
                label={`${data.commits.length} commits`}
                size="small"
                sx={{
                  background: 'rgba(0, 212, 255, 0.1)',
                  color: '#00d4ff',
                  border: '1px solid rgba(0, 212, 255, 0.2)',
                  fontSize: '0.7rem',
                  height: 22
                }}
              />
              <Box sx={{ height: '1px', flex: 1, background: 'linear-gradient(90deg, rgba(0,212,255,0.2), transparent)', ml: 1 }} />
            </Box>

            <Box sx={{ position: 'relative', pl: 3 }}>
              {/* Timeline Line */}
              <Box 
                sx={{ 
                  position: 'absolute', 
                  left: '7px', 
                  top: '10px', 
                  bottom: '10px', 
                  width: '2px', 
                  background: 'linear-gradient(180deg, #00d4ff 0%, rgba(0,212,255,0.1) 100%)',
                  opacity: 0.3,
                  borderRadius: '1px'
                }} 
              />

              {(() => {
                const pageCount = Math.ceil(data.commits.length / itemsPerPage);
                const paginatedCommits = data.commits.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                
                return (
                  <>
                    {paginatedCommits.map((commit, index) => {
                      const actualIndex = startIndex + index;
                      const isFirstCommit = actualIndex === 0;
                      
                      return (
                        <Box 
                          key={commit.hash} 
                          sx={{ 
                            position: 'relative', 
                            mb: 2.5,
                            animation: `${fadeInUp} ${0.3 + index * 0.1}s ease-out`
                          }}
                        >
                          {/* Timeline Dot */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '-28px',
                              top: '16px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: isFirstCommit ? '#00d4ff' : '#1a1a1a',
                              border: `2px solid ${isFirstCommit ? '#fff' : '#00d4ff'}`,
                              boxShadow: isFirstCommit ? '0 0 12px #00d4ff' : 'none',
                              zIndex: 1,
                              transition: 'all 0.3s ease'
                            }}
                          />

                          <Card
                            sx={{
                              background: 'rgba(255, 255, 255, 0.03)',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255, 255, 255, 0.06)',
                              borderRadius: '16px',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              cursor: commit.description ? 'pointer' : 'default',
                              '&:hover': {
                                background: 'rgba(255, 255, 255, 0.06)',
                                borderColor: 'rgba(0, 212, 255, 0.4)',
                                transform: 'translateX(6px)',
                                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                                '& .commit-hash': {
                                  background: '#00d4ff',
                                  color: '#000',
                                }
                              },
                            }}
                            onClick={() => commit.description && toggleCommitExpand(commit.hash)}
                          >
                            <CardContent sx={{ p: '16px !important' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                                  <Typography 
                                    variant="body1" 
                                    sx={{ 
                                      color: isFirstCommit ? '#fff' : 'rgba(255,255,255,0.9)', 
                                      fontWeight: isFirstCommit ? 600 : 500,
                                      lineHeight: 1.4,
                                      fontSize: '0.95rem'
                                    }}
                                  >
                                    {commit.message}
                                  </Typography>
                                  <Typography 
                                    className="commit-hash"
                                    variant="caption" 
                                    sx={{ 
                                      fontFamily: 'JetBrains Mono, Consolas, monospace',
                                      background: 'rgba(255,255,255,0.05)',
                                      color: 'rgba(255,255,255,0.5)',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: '6px',
                                      fontSize: '0.7rem',
                                      transition: 'all 0.2s ease',
                                      border: '1px solid rgba(255,255,255,0.05)'
                                    }}
                                  >
                                    {commit.hash}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CalendarToday sx={{ fontSize: 12 }} /> {formatDate(commit.date)}
                                    </Typography>
                                    {commit.description && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="caption" sx={{ color: '#00d4ff', fontWeight: 600, fontSize: '0.7rem' }}>
                                          {expandedCommits[commit.hash] ? 'COLLAPSE' : 'DETAILS'}
                                        </Typography>
                                        {expandedCommits[commit.hash] ? 
                                          <ExpandLess sx={{ fontSize: 14, color: '#00d4ff' }} /> : 
                                          <ExpandMore sx={{ fontSize: 14, color: '#00d4ff' }} />
                                        }
                                      </Box>
                                    )}
                                  </Box>
                                </Box>

                                {expandedCommits[commit.hash] && commit.description && (
                                  <Box 
                                    sx={{ 
                                      mt: 2, 
                                      p: 2, 
                                      background: 'rgba(0,0,0,0.3)', 
                                      borderRadius: '12px',
                                      borderLeft: '3px solid #00d4ff',
                                      animation: `${fadeInUp} 0.3s ease-out`
                                    }}
                                  >
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'rgba(255,255,255,0.7)',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'JetBrains Mono, Consolas, monospace',
                                        fontSize: '0.8rem',
                                        lineHeight: 1.6
                                      }}
                                    >
                                      {commit.description}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                    
                    {/* Pagination Footer */}
                    {pageCount > 1 && (
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'center', 
                          mt: 4, 
                          pt: 3,
                          borderTop: '1px solid rgba(255, 255, 255, 0.06)'
                        }}
                      >
                        <Pagination 
                          count={pageCount} 
                          page={currentPage} 
                          onChange={(e, v) => setCurrentPage(v)}
                          sx={{
                            '& .MuiPaginationItem-root': {
                              color: 'rgba(255, 255, 255, 0.7)',
                              backgroundColor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.08)',
                              borderRadius: '8px',
                              transition: 'all 0.2s ease',
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(0, 212, 255, 0.15)',
                                color: '#00d4ff',
                                border: '1px solid rgba(0, 212, 255, 0.3)',
                                fontWeight: 600,
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 212, 255, 0.25)',
                                }
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                borderColor: 'rgba(255, 255, 255, 0.15)',
                              }
                            },
                            '& .MuiPaginationItem-ellipsis': {
                              backgroundColor: 'transparent',
                              border: 'none',
                            }
                          }}
                        />
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const Maintenance = ({ apiBaseUrl, token, userInfo, onSave, isTrueNASOffline }) => {
  const [activeSubTab, setActiveSubTab] = useState(0);

  return (
    <Box>
      <GlassContainer sx={{ borderRadius: { xs: '16px', sm: '24px' } }}>
        <Box
          sx={{
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.02)',
            px: 0,
            overflowX: { xs: 'auto', sm: 'unset' },
          }}
        >
          <Tabs
            value={activeSubTab}
            onChange={(e, newValue) => setActiveSubTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: { xs: '48px', sm: '56px' },
              '& .MuiTabs-indicator': {
                background: (theme) => theme.palette.primary.main,
                height: '3px',
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.6)',
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                fontWeight: 500,
                py: { xs: 1, sm: 1.5 },
                px: { xs: 1.5, sm: 2.5 },
                minWidth: { xs: 'auto', sm: '100px' },
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.05)',
                },
                '&.Mui-selected': {
                  color: (theme) => theme.palette.primary.main,
                  background: (theme) => alpha(theme.palette.primary.main, 0.05),
                },
              },
            }}
          >
            <Tab label="📋 Logs" />
            <Tab label="🎨 GUI Theme" />
            <Tab label="🔐 Logon Page" />
            <Tab label="📜 Changelog" />
            <Tab label="🚀 Roadmap" />
            <Tab label="📌 Sidebar" />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {activeSubTab === 0 && (
            <MaintenanceLogs apiBaseUrl={apiBaseUrl} token={token} />
          )}
           {activeSubTab === 1 && (
             <GuiThemeSettings apiBaseUrl={apiBaseUrl} token={token} />
           )}
           {activeSubTab === 2 && (
             <LogonPageSettings apiBaseUrl={apiBaseUrl} token={token} />
           )}
           {activeSubTab === 3 && (
             <Changelog apiBaseUrl={apiBaseUrl} token={token} />
           )}
           {activeSubTab === 4 && (
             <Roadmap apiBaseUrl={apiBaseUrl} token={token} />
           )}
           {activeSubTab === 5 && (
             <SidebarSettings
               apiBaseUrl={apiBaseUrl}
               token={token}
               userInfo={userInfo}
               isTrueNASOffline={isTrueNASOffline}
               onSave={onSave}
             />
           )}
        </Box>
      </GlassContainer>
    </Box>
  );
};

export default Maintenance;
